import { envPublic } from '@/libs/config';
import { getRevoPushUpdateMetadata } from '@/libs/revopush';
import * as Sentry from '@sentry/react-native';
import { Platform } from 'react-native';
import DeviceInfo from 'react-native-device-info';

export const navigationIntegration = Sentry.reactNavigationIntegration({
  enableTimeToInitialDisplay: true,
});

const isNativeRuntime = Platform.OS === 'ios' || Platform.OS === 'android';

// Native build identity. release matches the format Sentry expects so the dashboard
// can group events by app version; dist defaults to the native build number — we
// don't override it with the CodePush label because sourcemaps are uploaded with
// --debug-id-reference and resolved via the bundle's embedded debug_id, not dist.
function resolveNativeRelease(): { release?: string; dist?: string } {
  if (!isNativeRuntime) return {};
  try {
    const bundleId = DeviceInfo.getBundleId();
    const version = DeviceInfo.getVersion();
    const buildNumber = DeviceInfo.getBuildNumber();
    return {
      release: `${bundleId}@${version}+${buildNumber}`,
      dist: buildNumber,
    };
  } catch {
    return {};
  }
}

export const sentryConfig: Sentry.ReactNativeOptions = {
  dsn: envPublic.sentryDsn,
  ...resolveNativeRelease(),
  environment: envPublic.environment || (__DEV__ ? 'development' : 'production'),

  enabled: !__DEV__,

  // Adds more context data to events (IP address, cookies, user, etc.)
  // For more information, visit: https://docs.sentry.io/platforms/react-native/data-management/data-collected/
  sendDefaultPii: false,

  beforeSend: event => {
    if (event.contexts?.user_meta?.providerIds) {
      event.contexts.user_meta.providerIds = event.contexts.user_meta.providerIds;
    }
    return event;
  },

  tracesSampleRate: __DEV__ ? 0 : 0.5,
  enableAutoPerformanceTracing: true,

  profilesSampleRate: __DEV__ ? 0 : 0.1,

  // Enable Logs
  enableLogs: true,

  // Configure Session Replay
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: __DEV__ ? 0 : 0,
  integrations: [
    // Disable because it can impact performance
    // Sentry.mobileReplayIntegration({
    //   maskAllText: true,
    //   maskAllImages: false,
    //   maskAllVectors: false,
    //   enableFastViewRendering: true,
    //   enableViewRendererV2: true,
    // }),
    Sentry.feedbackIntegration(),
    navigationIntegration,
  ],

  // uncomment the line below to enable Spotlight (https://spotlightjs.com)
  // spotlight: __DEV__,
};

// Attach the currently-running RevoPush bundle label so dashboard events can be
// filtered by hot-update version. Safe to fire-and-forget right after Sentry.init().
export async function applyRevoPushSentryContext(): Promise<void> {
  if (!isNativeRuntime) return;
  try {
    const meta = await getRevoPushUpdateMetadata();
    if (!meta) return;

    const scope = Sentry.getGlobalScope();
    scope.setTag('codepush.label', meta.label);
    scope.setTag('codepush.deployment', meta.deploymentKey);
    scope.setContext('codepush', {
      label: meta.label,
      description: meta.description,
      appVersion: meta.appVersion,
      isMandatory: meta.isMandatory,
      isFirstRun: meta.isFirstRun,
    });
  } catch {
    // No update installed yet (first launch) — leave scope as-is.
  }
}
